import React from 'react';
import Helmet from 'react-helmet';
import ClipLoader from 'react-spinners/ClipLoader';
import _ from 'lodash';
import moment from 'moment';
import Joi from 'joi-browser';

import { PrimeCCUSPreviewService } from '../services/PrimeCCUSPreview';
import { CompanyService } from '../services/Companies';
import LocalStorageService from '../services/utilities/LocalStorageService';
import SessionStorageService from '../services/utilities/SessionStorageService';
import {
  DATE_TIME_FORMAT,
  HEADER_CONSTANTS,
  STATUS_CODE,
  TIME_OUT_TWO_SECOND,
} from '../utilities/constants';
import {
  isLoginUserPartner,
  isPartnerRoleId,
  isPrimaryCompanyRoleId,
} from '../utilities/roleUtility';
import { messageUtility } from '../utilities/messageUtility';
import dialogLoader from '../utilities/dialogLoader';
import _masterSlideLoader from '../utilities/masterSlideLoader';
import regex from '../utilities/validateRegex';
import { validationRegexByJoi } from '../utilities/commonValidation';
import { setWaitTime, getCompanyId } from '../utilities/commonUtil';
import {
  getPartnerCompanyName,
  propsToCompanyId,
} from '../utilities/partnerCompanyConfirmUtil';
import { CCUS_PAGE, CCUS_STATUS } from './PrimeCcusPreview';

import { CcusAdminForm } from '../components/forms/CcusAdminForm';
import { PartnerNaviList } from '../components/list/PartnerNaviList';
import { PrimaryCompanyNaviList } from '../components/list/PrimaryCompanyNaviList';
import { MasterPageHeader } from '../components/PageHeader';
import { Confirmation, Information } from '../components/modals/common';
import { CloseEditView } from '../components/CloseEditView';
import {
  WarningNotConnectCcus,
  PopupErrorConnectCcus,
} from '../components/CcusBase';

const getMessageOfRole = () => {
  if (isPrimaryCompanyRoleId(LocalStorageService.getRoleId())) {
    return '続けてCCUSへの現場情報の登録および取得、施工体制の登録が可能となる連携ユーザーを設定してください。';
  }
  return '続けてCCUSから事業者情報、技能者情報の取得が可能となる連携ユーザーを設定してください。';
};

class CcusAdmin extends React.Component {
  constructor(props) {
    super(props);

    this.ownerCompanyId = getCompanyId();
    this.selectedCompanyId = SessionStorageService.getItem('companyCcusId');
    this.isOwner = this.ownerCompanyId === this.selectedCompanyId;
    this.ccusId = propsToCompanyId(this.props);
    this.isRolePartner = isPartnerRoleId(LocalStorageService.getRoleId());
    this.isPrimaryCompanyRole = isPrimaryCompanyRoleId(
      LocalStorageService.getRoleId()
    );

    this.state = {
      isEditable: false,
      visibleDialogName: null,
      statusMessage: null,
      formAdminValues: {
        ccus_company_admin_id: '',
        ccus_company_admin_password: '',
        ccus_company_admin_name: '',
        connection_status: null,
        last_connected_at: '',
      },
      isLoading: false,
      selectedCompanyName: '',
      validate: {
        invalid: true,
        messages: {},
      },
      messageError: '',
      isValidateSelect: false,
      isLoadingCcus: false,
      ccusStatus: null,
      isShowWarning: false,
      isShowErrorConnectCcus: false,
      isShowCreateSuccess: false,
      isShowPass: false,
      ccusStateManagementId: null,
      lastConnectedAt: null,
      messageNG: '',
      isLoadingData: false,
      isShowModalDelete: false,
      isErrorDelete: false,
      expirationCompany: {},
    };

    this.connectingCcusFlag = false; // CCUS接続確認ボタン連打防止Flag

    this.events = {
      goBack: this.goBack,
    };

    this.goPreviousPage = {
      callback: {
        clear: () => {
          _masterSlideLoader.show('PrimeCcusPreview', {
            state: { id: this.selectedCompanyId },
          });
          dialogLoader.close();
        },
        close: () => {
          dialogLoader.close();
        },
      },
    };

    this.page = this.props.page;
  }

  getExpiration = async () => {
    await CompanyService.expirations(this.selectedCompanyId).then(response => {
      if (!response.result) {
        return;
      }
      this.setState({
        expirationCompany: {
          ...this.state.expirationCompany,
          ...response.data.entities.company.expiration,
        },
      });
    });
  };

  componentDidMount() {
    this.getInfo();
    this.getExpiration();
  }

  goBack = (showDialog = false) => {
    this.setState({ isShowCreateSuccess: false });
    if (showDialog) {
      dialogLoader.show(CloseEditView, this.goPreviousPage);
    } else {
      _masterSlideLoader.show('PrimeCcusPreview', {
        state: { id: this.selectedCompanyId },
      });
    }
  };

  initSchemaAdmin = () => {
    const isRequiredPass =
      (this.page === CCUS_PAGE.CcusAdminEdit && this.state.isShowPass) ||
      this.page === CCUS_PAGE.CcusAdminCreate;
    return Joi.object().keys({
      ccus_company_admin_name: Joi.string()
        .required()
        .max(14),
      ccus_company_admin_id: Joi.string()
        .regex(regex.pattern.pwd)
        .max(14)
        .required(),
      ccus_company_admin_password: isRequiredPass
        ? Joi.string()
            .regex(regex.pattern.noSpace)
            .max(24)
            .required()
        : Joi.any(),
    });
  };

  setCompanyName = () => {
    this.setState(
      {
        isLoading: true,
      },
      async () => {
        const companyName = await getPartnerCompanyName(this.selectedCompanyId);
        this.setState({ selectedCompanyName: companyName, isLoading: false });
      }
    );
  };

  handleVisibleDialog = dialogName => {
    this.setState({ visibleDialogName: dialogName });
  };

  showStatusDialog = statusMessage => {
    this.handleVisibleDialog(true);
    this.setState({ statusMessage });
  };

  handleOnChange = (e, key) => {
    const { formAdminValues } = this.state;
    const { value } = e.target;
    this.setState({
      formAdminValues: {
        ...formAdminValues,
        [key]: value,
      },
    });
  };

  validate = (e, targetName) => {
    const values = _.cloneDeep(this.state.formAdminValues);
    const schema = this.initSchemaAdmin();
    const validate = _.cloneDeep(this.state.validate);

    const result = validationRegexByJoi(schema, values, true);
    if (e || targetName) {
      const name = _.isEmpty(targetName) ? e.target.name : targetName;
      if (result.messages[name]) {
        validate.messages[name] = result.messages[name];
      } else if (validate.messages[name]) {
        delete validate.messages[name];
      }
    }
    validate.invalid = !_.isEmpty(result.messages);
    if (targetName !== 'ccus_company_admin_name') {
      let ccusStatus = null;
      if (
        !validate.invalid ||
        (validate.invalid &&
          Object.keys(result.messages).length === 1 &&
          _.get(result.messages, 'ccus_company_admin_name'))
      ) {
        ccusStatus = CCUS_STATUS.DEFAULT;
      }
      this.setState({
        ccusStatus,
      });
    }
    this.setState({ validate });
    return validate.invalid;
  };

  validateAll = () => {
    const values = _.cloneDeep(this.state.formAdminValues);
    const schema = this.initSchemaAdmin();
    const validate = _.cloneDeep(this.state.validate);

    const result = validationRegexByJoi(schema, values, true);
    if (!result.status) {
      this.setState({
        validate: {
          invalid: !result.status,
          messages: {
            ...validate.messages,
            ...result.messages,
          },
        },
      });
    }
  };

  connectCcus = async value => {
    // BLDE_V2-37869: CCUS接続確認ボタン連打防止。ダイアログが開いている間も連打禁止
    if (this.connectingCcusFlag || this.state.isLoadingCcus) {
      return;
    }
    this.connectingCcusFlag = true;
    if (value.ccus_company_admin_password === '') {
      value.ccus_company_admin_password = undefined;
    }

    this.setState({ isLoadingCcus: true });
    const response = await PrimeCCUSPreviewService.connectConfirmation(
      this.selectedCompanyId,
      value
    );
    if (response.result) {
      const ccusStateManagementId = _.get(
        response.data,
        'entities.ccus_state_management_id',
        null
      );
      this.monitoringFetchCcusCompanyData(
        ccusStateManagementId,
        this.selectedCompanyId
      );
    } else {
      this.setState({
        isLoadingCcus: false,
        isShowErrorConnectCcus: true,
        messageError: _.get(response.data, 'message'),
      });
    }
    this.connectingCcusFlag = false;
  };

  monitoringFetchCcusCompanyData = (ccusStateManagementId, companyId) => {
    setWaitTime(TIME_OUT_TWO_SECOND).then(async () => {
      const params = {
        ccus_state_management_id: ccusStateManagementId,
      };
      const response = await PrimeCCUSPreviewService.connectCcusFetchData(
        companyId,
        params
      );
      if (response.result) {
        if (response.status === STATUS_CODE.ok) {
          this.setState({
            isLoadingCcus: false,
            ccusStatus: CCUS_STATUS.OK,
            ccusStateManagementId,
            lastConnectedAt: moment(new Date()).format(
              DATE_TIME_FORMAT.slashDateFormat
            ),
            messageNG: '',
          });
        } else if (response.status === STATUS_CODE.accepted) {
          this.monitoringFetchCcusCompanyData(ccusStateManagementId, companyId);
        }
      } else {
        if (response.status === STATUS_CODE.badRequest) {
          return this.setState({
            isLoadingCcus: false,
            ccusStatus: CCUS_STATUS.NG,
            ccusStateManagementId,
            messageNG:
              _.get(response, 'message') || _.get(response.data, 'message'),
          });
        }
        this.setState({
          isLoadingCcus: false,
          isShowErrorConnectCcus: true,
          messageError: _.get(response.data, 'message', null),
        });
      }
    });
  };

  removeMsgPasswords = isShowPass => {
    const { validate, formAdminValues } = this.state;
    if (_.get(validate, 'messages.ccus_company_admin_password')) {
      delete validate.messages.ccus_company_admin_password;
    }
    if (isShowPass) {
      validate.invalid = !_.isEmpty(validate.messages);
    } else {
      validate.invalid =
        !_.isEmpty(validate.messages) ||
        !_.get(formAdminValues, 'ccus_company_admin_password', '');
    }
    this.setState({ validate });
  };

  toggleShowPassword = isShowPass => {
    this.removeMsgPasswords(isShowPass);
    const formAdminValues = _.cloneDeep(this.state.formAdminValues);
    let ccusStatus = CCUS_STATUS.DEFAULT;
    if (!isShowPass) {
      ccusStatus = null;
    }
    this.setState({
      formAdminValues: {
        ...formAdminValues,
        ccus_company_admin_password: '',
      },
      isShowPass: !this.state.isShowPass,
      ccusStatus,
    });
  };

  getInfo = () => {
    if (isLoginUserPartner()) {
      this.setCompanyName();
    }
    if (this.page === CCUS_PAGE.CcusAdminEdit) {
      this.getCcusAddionInfo();
    }
  };

  getCcusAddionInfo = async () => {
    this.setState({ isLoadingData: true });
    const response = await PrimeCCUSPreviewService.getAdditionInfo(
      this.selectedCompanyId,
      this.ccusId
    );
    this.setState({ isLoadingData: false });
    if (response.result) {
      const {
        ccus_company_admin_id,
        ccus_company_admin_name,
        connection_status,
        last_connected_at,
        id,
      } = response.data.entities.ccus_company_admin;
      const { formAdminValues, validate } = this.state;
      this.setState({
        formAdminValues: {
          ...formAdminValues,
          ccus_company_admin_id,
          ccus_company_admin_name,
          connection_status,
          last_connected_at,
          id,
        },
        ccusStatus: connection_status,
        validate: {
          ...validate,
          invalid: false,
        },
      });
    }
  };

  handleOnSubmit = () => {
    this.validateAll();
    const {
      validate: { invalid },
    } = this.state;
    if (invalid) {
      return;
    }

    if (this.state.ccusStatus !== CCUS_STATUS.OK) {
      return this.setState({ isShowWarning: true });
    }

    if (this.page === CCUS_PAGE.CcusAdminCreate) {
      return this.createCcusAdditionInfo();
    }
    this.updateCcusAdditionInfo();
  };

  createCcusAdditionInfo = async () => {
    const formAdminValues = _.cloneDeep(this.state.formAdminValues);
    const {
      isLoading,
      validate: { invalid },
      ccusStatus,
      lastConnectedAt,
      ccusStateManagementId,
    } = this.state;
    if (isLoading || invalid) {
      return;
    }
    const data = {
      ccus_company_admin_id: formAdminValues.ccus_company_admin_id,
      ccus_company_admin_password: formAdminValues.ccus_company_admin_password,
      ccus_company_admin_name: formAdminValues.ccus_company_admin_name,
      connection_status: _.isNull(ccusStatus)
        ? CCUS_STATUS.DEFAULT
        : ccusStatus,
    };
    if (ccusStatus === CCUS_STATUS.OK) {
      data.last_connected_at = lastConnectedAt;
    }
    if (ccusStateManagementId) {
      data.ccus_state_management_id = ccusStateManagementId;
    }

    const response = await PrimeCCUSPreviewService.createAdditionInfo(
      this.selectedCompanyId,
      data
    );
    this.setState({ isShowWarning: false });
    if (response.result) {
      return this.setState({
        isShowCreateSuccess: true,
        formAdminValues: {
          ...formAdminValues,
          id: _.get(response.data, 'entities.ccus_company_admin.id'),
        },
      });
    } else {
      this.showStatusDialog(_.get(response.data, 'message'));
    }
  };

  updateCcusAdditionInfo = async () => {
    const {
      isLoading,
      validate: { invalid },
      formAdminValues,
      ccusStatus,
      lastConnectedAt,
      ccusStateManagementId,
      isShowPass,
    } = this.state;
    if (isLoading || invalid) {
      return;
    }

    this.setState({ isLoading: true, isShowWarning: false });
    const data = {
      ccus_company_admin_id: formAdminValues.ccus_company_admin_id,
      ccus_company_admin_name: formAdminValues.ccus_company_admin_name,
    };
    if (ccusStateManagementId) {
      data.ccus_state_management_id = ccusStateManagementId;
      data.connection_status = _.isNull(ccusStatus)
        ? CCUS_STATUS.DEFAULT
        : ccusStatus;
    }
    if (ccusStatus === CCUS_STATUS.DEFAULT) {
      data.connection_status = CCUS_STATUS.DEFAULT;
    }
    if (ccusStatus === CCUS_STATUS.OK && ccusStateManagementId) {
      data.last_connected_at = lastConnectedAt;
    }
    if (formAdminValues.ccus_company_admin_password && isShowPass) {
      data.ccus_company_admin_password =
        formAdminValues.ccus_company_admin_password;
    }
    const response = await PrimeCCUSPreviewService.updateAdditionInfo(
      this.selectedCompanyId,
      this.ccusId,
      data
    );

    if (response.result) {
      this.goBack();
    } else {
      this.showStatusDialog(response.data.message);
    }
  };

  handleOnConfirm = () => {
    if (this.page === CCUS_PAGE.CcusAdminCreate) {
      return this.createCcusAdditionInfo();
    }
    this.updateCcusAdditionInfo();
  };

  goToListUser = () => {
    _masterSlideLoader.show(CCUS_PAGE.CcusAdminUserEdit, {
      state: {
        id: _.get(this.state.formAdminValues, 'id'),
      },
    });
  };

  handleDelete = async () => {
    const { result } = await PrimeCCUSPreviewService.deleteAdditionInfo(
      this.selectedCompanyId,
      this.ccusId
    );
    if (result) {
      return this.goBack();
    }
    this.setState({ isErrorDelete: true });
  };

  toggleModalDelete = () => {
    this.setState({ isShowModalDelete: !this.state.isShowModalDelete });
  };

  getMessageDelete = () => {
    return (
      <React.Fragment>
        CCUS管理者IDの削除に失敗しました。
        <br />
        CCUS連携情報に戻ります。再度操作を行ってください。
      </React.Fragment>
    );
  };
  render() {
    const {
      isLoading,
      selectedCompanyName,
      formAdminValues,
      validate: { messages, invalid },
      isLoadingCcus,
      ccusStatus,
      isShowErrorConnectCcus,
      messageError,
      isShowCreateSuccess,
      ccusStateManagementId,
      isShowPass,
      messageNG,
      isLoadingData,
      isShowModalDelete,
    } = this.state;

    const title = `${selectedCompanyName}会社情報`;

    return (
      <div id='slide-view' className='contruction-slide-view show CcusAdmin'>
        <Helmet>
          <title>
            {HEADER_CONSTANTS.title} - {title}
          </title>
        </Helmet>
        <div className='contruction-slide-view__view'>
          <MasterPageHeader
            loaded={!isLoading}
            loaderComponent={
              <div className='contruction-slide-view__title'>
                <ClipLoader size={20} color='#EDB715' />
              </div>
            }
            title={title}
            onClose={() => this.goBack(true)}
          />
          <div className='contruction-slide-view__body contruction-slide-view__body--height-fix'>
            <div className='contruction-detail-view'>
              <div className='contruction-detail-view__sidemenu'>
                {this.isPrimaryCompanyRole && (
                  <PrimaryCompanyNaviList
                    isSelectedCcusManagementInfo={true}
                    isEditable={true}
                    expirationCompany={this.state.expirationCompany}
                  />
                )}
                {this.isRolePartner && (
                  <PartnerNaviList
                    isCcusManagementInfo={true}
                    isEditable={true}
                    selectedPartnerCompanyId={this.selectedCompanyId}
                    expirationCompany={this.state.expirationCompany}
                  />
                )}
              </div>
              <div className='contruction-detail-view__body contruction-detail-view__body--fixed'>
                <div className='contruction-detail-view__menu'>
                  <div>
                    <span className='construction-detail-view__button'>
                      <button
                        className='btn--sub'
                        onClick={() => this.goBack(true)}
                      >
                        {this.page === CCUS_PAGE.CcusAdminCreate
                          ? '登録をやめる'
                          : '編集をやめる'}
                      </button>
                    </span>
                    <span className='construction-detail-view__button'>
                      <button
                        className='btn--effective'
                        onClick={this.handleOnSubmit}
                      >
                        {this.page === CCUS_PAGE.CcusAdminCreate
                          ? '登録'
                          : '更新'}
                      </button>
                    </span>
                  </div>
                  {this.page === CCUS_PAGE.CcusAdminEdit && (
                    <div>
                      <button
                        className='btn--negative'
                        onClick={this.toggleModalDelete}
                      >
                        IDを削除
                      </button>
                    </div>
                  )}
                </div>
                <div className='contruction-detail-view__bar' />
                <div className='contruction-detail-view__main contruction-detail-view__main--fixed contruction-detail-view__main--fix'>
                  <CcusAdminForm
                    editPage={this.page === CCUS_PAGE.CcusAdminEdit}
                    data={formAdminValues}
                    setValue={this.handleOnChange}
                    messageValidate={messages}
                    invalid={invalid}
                    checkValidate={this.validate}
                    connectCcus={this.connectCcus}
                    isLoadingCcus={isLoadingCcus}
                    ccusStatus={ccusStatus}
                    ccusStateManagementId={ccusStateManagementId}
                    toggleShowPassword={this.toggleShowPassword}
                    isShowPass={isShowPass}
                    messageNG={messageNG}
                    isLoadingData={isLoadingData}
                    isOwner={this.isOwner}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <Information
          message={this.state.statusMessage}
          buttonText={messageUtility.getMessage('I-000-011')}
          visible={this.state.visibleDialogName}
          onClose={() => this.handleVisibleDialog(false)}
        />
        <WarningNotConnectCcus
          isShowWarning={this.state.isShowWarning}
          onClose={() => this.setState({ isShowWarning: false })}
          onConfirm={this.handleOnConfirm}
        />

        <PopupErrorConnectCcus
          onClose={() => this.setState({ isShowErrorConnectCcus: false })}
          isShowPopup={isShowErrorConnectCcus}
          message={messageError}
        />

        <Confirmation
          message={getMessageOfRole()}
          title='CCUS基本情報登録完了'
          cancelButtonText='閉じる'
          confirmButtonText='連携ユーザーを登録'
          visible={isShowCreateSuccess}
          onCancel={() => this.goBack(false)}
          onConfirm={this.goToListUser}
        />

        <Confirmation
          message='CCUS管理者IDを削除してよろしいですか？'
          title='CCUS管理者IDを削除する'
          cancelButtonText='戻る'
          confirmButtonText='削除する'
          isNegative={true}
          visible={isShowModalDelete}
          onCancel={this.toggleModalDelete}
          onConfirm={this.handleDelete}
        />

        <Information
          message={this.getMessageDelete()}
          title={messageUtility.getMessage('E-001-402')}
          buttonText='閉じる'
          visible={this.state.isErrorDelete}
          onClose={() => this.goBack(false)}
          multilineText={true}
        />
      </div>
    );
  }
}

export class CcusAdminCreate extends React.Component {
  render() {
    return <CcusAdmin {...this.props} page={CCUS_PAGE.CcusAdminCreate} />;
  }
}

export class CcusAdminEdit extends React.Component {
  render() {
    return <CcusAdmin {...this.props} page={CCUS_PAGE.CcusAdminEdit} />;
  }
}



// WEBPACK FOOTER //
// ./src/pages/CcusAdmin.jsx